import { createApi } from '@reduxjs/toolkit/query/react';
import { CacheTags, PaginatedRequest } from 'types';
import { HourSeconds, OdinBaseQuery } from './core';

// Import transaction-related types
import { PostAfeTransactionRequest } from './bindings/PostAfeTransactionRequest';
import { PostDecisionRequest } from './bindings/PostDecisionRequest';
import { InvoiceTransactionSummaryResponse } from './bindings/InvoiceTransactionSummaryResponse';
import { WorkOrderTransactionSummaryResponse } from './bindings/WorkOrderTransactionSummaryResponse';
import { PostWorkOrderTransactionRequest } from './bindings/PostWorkOrderTransactionRequest';
import { PurchaseOrderTransactionSummaryResponse } from './bindings/PurchaseOrderTransactionSummaryResponse';
import { PostInvoiceTransactionRequest } from './bindings/PostInvoiceTransactionRequest';
import { PostPurchaseOrderTransactionRequest } from './bindings/PostPurchaseOrderTransactionRequest';
import { FetchResponse } from './bindings/FetchResponse';
import { AfeTransactionSummaryResponse } from './bindings/AfeTransactionSummaryResponse';

// Type aliases for better readability
type AfeTransactionsResponse = FetchResponse<AfeTransactionSummaryResponse>;
type InvoiceTransactionsResponse =
  FetchResponse<InvoiceTransactionSummaryResponse>;
type WorkOrderTransactionsResponse =
  FetchResponse<WorkOrderTransactionSummaryResponse>;
type PurchaseOrderTransactionsResponse =
  FetchResponse<PurchaseOrderTransactionSummaryResponse>;

export const transactionsApi = createApi({
  reducerPath: 'transactions',
  baseQuery: OdinBaseQuery,
  tagTypes: [
    CacheTags.afes,
    CacheTags.afeDetail,
    CacheTags.afeTxn,
    CacheTags.invoices,
    CacheTags.invoiceDetail,
    CacheTags.invoiceTxn,
    CacheTags.workOrders,
    CacheTags.workOrderDetail,
    CacheTags.workOrderTxn,
    CacheTags.purchaseOrders,
    CacheTags.purchaseOrderDetail,
    CacheTags.purchaseOrderTxn,
  ],
  endpoints: (builder) => ({
    fetchAfeTransactions: builder.query<
      AfeTransactionsResponse,
      PaginatedRequest & { afeId: string }
    >({
      keepUnusedDataFor: HourSeconds,
      query: (payload) => ({
        url: `/api/afes/${payload.afeId}/transactions`,
        method: 'GET',
        params: payload,
      }),
      providesTags: (data) => {
        let returnValue = [{ type: CacheTags.afeTxn, id: CacheTags.partial }];
        if (data) {
          returnValue = [
            ...returnValue,
            ...data.items.map(({ id }) => ({
              type: CacheTags.afeTxn,
              id: id,
            })),
          ];
        }
        return returnValue;
      },
    }),
    postAfeTransactionDecision: builder.mutation<
      AfeTransactionsResponse,
      PostDecisionRequest & { entityId: string; transactionId: string }
    >({
      query: (payload) => ({
        url: `/api/afes/${payload.entityId}/transactions/${payload.transactionId}`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (result, error, payload) => [
        { type: CacheTags.afeTxn, id: CacheTags.partial },
        { type: CacheTags.afeTxn, id: payload.transactionId },
        { type: CacheTags.afeDetail, id: payload.entityId },
        { type: CacheTags.afes, id: payload.entityId },
        { type: CacheTags.afes, id: CacheTags.partial },
      ],
    }),

    postAfeTransaction: builder.mutation<
      AfeTransactionsResponse,
      PostAfeTransactionRequest & { entityId: string }
    >({
      query: (payload) => ({
        url: `/api/afes/${payload.entityId}/transactions`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (result, error, payload) => [
        { type: CacheTags.afeTxn, id: CacheTags.partial },
      ],
    }),

    postInvoiceTransactionDecision: builder.mutation<
      InvoiceTransactionsResponse,
      PostDecisionRequest & { entityId: string; transactionId: string }
    >({
      query: (payload) => ({
        url: `/api/invoices/${payload.entityId}/transactions/${payload.transactionId}`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (result, error, payload) => [
        { type: CacheTags.invoiceTxn, id: CacheTags.partial },
        { type: CacheTags.invoiceTxn, id: payload.transactionId },
        { type: CacheTags.invoiceDetail, id: payload.entityId },
        { type: CacheTags.invoices, id: payload.entityId },
        { type: CacheTags.invoices, id: CacheTags.partial },
      ],
    }),

    postInvoiceTransaction: builder.mutation<
      InvoiceTransactionsResponse,
      PostInvoiceTransactionRequest & { entityId: string }
    >({
      query: (payload) => ({
        url: `/api/invoices/${payload.entityId}/transactions`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (result, error, payload) => [
        { type: CacheTags.invoiceTxn, id: CacheTags.partial },
      ],
    }),

    fetchInvoiceTransactions: builder.query<
      InvoiceTransactionsResponse,
      PaginatedRequest & { entityId: string }
    >({
      keepUnusedDataFor: HourSeconds,
      query: (payload) => ({
        url: `/api/invoices/${payload.entityId}/transactions`,
        method: 'GET',
        params: payload,
      }),
      providesTags: (data) => {
        let returnValue = [
          { type: CacheTags.invoiceTxn, id: CacheTags.partial },
        ];
        if (data) {
          returnValue = [
            ...returnValue,
            ...data.items.map(({ id }) => ({
              type: CacheTags.invoiceTxn,
              id: id,
            })),
          ];
        }
        return returnValue;
      },
    }),

    postWorkOrderTransactionDecision: builder.mutation<
      WorkOrderTransactionsResponse,
      PostDecisionRequest & { entityId: string; transactionId: string }
    >({
      query: (payload) => ({
        url: `/api/work-orders/${payload.entityId}/transactions/${payload.transactionId}`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (result, error, payload) => [
        { type: CacheTags.workOrderTxn, id: CacheTags.partial },
        { type: CacheTags.workOrderTxn, id: payload.transactionId },
        { type: CacheTags.workOrderDetail, id: payload.entityId },
        { type: CacheTags.workOrders, id: payload.entityId },
        { type: CacheTags.workOrders, id: CacheTags.partial },
      ],
    }),

    postWorkOrderTransaction: builder.mutation<
      WorkOrderTransactionsResponse,
      PostWorkOrderTransactionRequest & { entityId: string }
    >({
      query: (payload) => ({
        url: `/api/work-orders/${payload.entityId}/transactions`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (result, error, payload) => [
        { type: CacheTags.workOrderTxn, id: CacheTags.partial },
      ],
    }),

    fetchWorkOrderTransactions: builder.query<
      WorkOrderTransactionsResponse,
      PaginatedRequest & { entityId: string }
    >({
      keepUnusedDataFor: HourSeconds,
      query: (payload) => ({
        url: `/api/work-orders/${payload.entityId}/transactions`,
        method: 'GET',
        params: payload,
      }),
      providesTags: (data) => {
        let returnValue = [
          { type: CacheTags.workOrderTxn, id: CacheTags.partial },
        ];
        if (data) {
          returnValue = [
            ...returnValue,
            ...data.items.map(({ id }) => ({
              type: CacheTags.workOrderTxn,
              id: id,
            })),
          ];
        }
        return returnValue;
      },
    }),

    postPurchaseOrderTransactionDecision: builder.mutation<
      PurchaseOrderTransactionsResponse,
      PostDecisionRequest & {
        entityId: string;
        transactionId: string;
      }
    >({
      query: (payload) => ({
        url: `/api/purchase-orders/${payload.entityId}/transactions/${payload.transactionId}`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (result, error, payload) => [
        { type: CacheTags.purchaseOrderTxn, id: CacheTags.partial },
        {
          type: CacheTags.purchaseOrderTxn,
          id: payload.transactionId,
        },
        {
          type: CacheTags.purchaseOrderDetail,
          id: payload.entityId,
        },
        {
          type: CacheTags.purchaseOrders,
          id: payload.entityId,
        },
        { type: CacheTags.purchaseOrders, id: CacheTags.partial },
      ],
    }),

    postPurchaseOrderTransaction: builder.mutation<
      PurchaseOrderTransactionsResponse,
      PostPurchaseOrderTransactionRequest & { entityId: string }
    >({
      query: (payload) => ({
        url: `/api/purchase-orders/${payload.entityId}/transactions`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (result, error, payload) => [
        { type: CacheTags.purchaseOrderTxn, id: CacheTags.partial },
      ],
    }),

    fetchPurchaseOrderTransactions: builder.query<
      PurchaseOrderTransactionsResponse,
      PaginatedRequest & { entityId: string }
    >({
      keepUnusedDataFor: HourSeconds,
      query: (payload) => ({
        url: `/api/purchase-orders/${payload.entityId}/transactions`,
        method: 'GET',
        params: payload,
      }),
      providesTags: (data) => {
        let returnValue = [
          { type: CacheTags.purchaseOrderTxn, id: CacheTags.partial },
        ];
        if (data) {
          returnValue = [
            ...returnValue,
            ...data.items.map(({ id }) => ({
              type: CacheTags.purchaseOrderTxn,
              id: id,
            })),
          ];
        }
        return returnValue;
      },
    }),

    // All transaction endpoints are commented out for reference
    // Future transaction functionality will be implemented here
    // AFE Transaction endpoints (moved from afesApi.ts)
    // postAfeTransaction: builder.mutation<
    //   AllTransactionSummary,
    //   PostAfeTransactionRequest
    // >({
    //   query: (payload) => ({
    //     url: `/api/afes/${(payload as any).afeId}/transactions`,
    //     method: 'POST',
    //     body: payload,
    //   }),
    //   invalidatesTags: (result, error, payload) => [
    //     { type: CacheTags.afes, id: CacheTags.partial },
    //     {
    //       type: CacheTags.afeDetail,
    //       id: (payload as any).afeId?.toString() ?? CacheTags.partial,
    //     },
    //   ],
    // }),
    // postAfeTxnDecision: builder.mutation<any, PostDecisionRequest>({
    //   query: (payload) => ({
    //     url: '/api/approvals',
    //     method: 'POST',
    //     body: payload,
    //   }),
    //   invalidatesTags: () => [
    //     { type: CacheTags.afeDetail, id: CacheTags.partial },
    //   ],
    // }),
    // Invoice Transaction endpoints (moved from invoicesApi.ts)
    // postInvoiceTransaction: builder.mutation<
    //   InvoiceTransactionSummaryResponse,
    //   InvoiceTransactionSummaryResponse
    // >({
    //   query: (payload) => ({
    //     url: `/api/invoices/${payload.entityId}/transactions`,
    //     method: 'POST',
    //     body: payload,
    //   }),
    //   invalidatesTags: (result) => [
    //     { type: CacheTags.invoices, id: CacheTags.partial },
    //     { type: CacheTags.invoiceDetail, id: result?.entityId?.toString() },
    //   ],
    // }),
    // postInvoiceTxnDecision: builder.mutation<
    //   any,
    //   { entityId: bigint; data: PostDecisionRequest }
    // >({
    //   query: (payload) => ({
    //     url: '/api/approvals',
    //     method: 'POST',
    //     body: payload,
    //   }),
    //   invalidatesTags: (result, error, payload) => [
    //     { type: CacheTags.invoiceDetail, id: payload.entityId.toString() },
    //   ],
    // }),
    // Work Order Transaction endpoints (moved from workOrdersApi.ts)
    // postWorkOrderTransaction: builder.mutation<
    //   WorkOrderTransactionSummaryResponse,
    //   { workOrderId: bigint } & PostWorkOrderTransactionRequest
    // >({
    //   query: (payload) => ({
    //     url: `/api/work-orders/${payload.workOrderId}/transactions`,
    //     method: 'POST',
    //     body: payload,
    //   }),
    //   invalidatesTags: (result, error, payload) => [
    //     { type: CacheTags.workOrders, id: CacheTags.partial },
    //     { type: CacheTags.workOrders, id: payload?.workOrderId?.toString() },
    //     {
    //       type: CacheTags.workOrderDetail,
    //       id: payload?.workOrderId?.toString(),
    //     },
    //   ],
    // }),
    // postWorkOrderTxnDecision: builder.mutation<
    //   any,
    //   { entityId: bigint } & PostDecisionRequest
    // >({
    //   query: (payload) => ({
    //     url: '/api/approvals',
    //     method: 'POST',
    //     body: payload,
    //   }),
    //   invalidatesTags: (result, error, payload) => [
    //     { type: CacheTags.workOrderDetail, id: payload.entityId?.toString() },
    //   ],
    // }),
    // Purchase Order Transaction endpoints (moved from purchaseOrdersApi.ts)
    // postPurchaseOrderTransaction: builder.mutation<
    //   PurchaseOrderTransactionSummaryResponse,
    //   any
    // >({
    //   query: (payload) => ({
    //     url: `/api/purchase-orders/${payload.purchaseOrderId}/transactions`,
    //     method: 'POST',
    //     body: payload,
    //   }),
    //   invalidatesTags: (result, error, payload) => [
    //     { type: CacheTags.purchaseOrders, id: CacheTags.partial },
    //     { type: CacheTags.purchaseOrderDetail, id: payload?.purchaseOrderId?.toString() },
    //   ],
    // }),
    // postPurchaseOrderTxnDecision: builder.mutation<any, PostDecisionRequest>({
    //   query: (payload) => ({
    //     url: '/api/approvals',
    //     method: 'POST',
    //     body: payload,
    //   }),
    //   invalidatesTags: () => [
    //     { type: CacheTags.purchaseOrders, id: CacheTags.partial },
    //     { type: CacheTags.purchaseOrderDetail, id: CacheTags.partial },
    //   ],
    // }),
  }),
});

export const {
  // Fetch transaction hooks
  useFetchAfeTransactionsQuery,
  useFetchInvoiceTransactionsQuery,
  useFetchWorkOrderTransactionsQuery,
  useFetchPurchaseOrderTransactionsQuery,

  // Transaction mutation hooks
  // AFE Transaction hooks
  usePostAfeTransactionMutation,
  usePostAfeTransactionDecisionMutation,

  // Invoice Transaction hooks
  usePostInvoiceTransactionMutation,
  usePostInvoiceTransactionDecisionMutation,

  // Work Order Transaction hooks
  usePostWorkOrderTransactionMutation,
  usePostWorkOrderTransactionDecisionMutation,

  // Purchase Order Transaction hooks
  usePostPurchaseOrderTransactionMutation,
  usePostPurchaseOrderTransactionDecisionMutation,
} = transactionsApi;
